import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAccessToken,
  getRefreshToken,
  getTokenExpiresAt,
  setTokens,
  clearTokens,
} from '../lib/api';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    accessToken: string,
    refreshToken: string,
    expiresAt?: number
  ) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(
  null
);

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function initAuth() {
      const token = getAccessToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      const expiresAt = getTokenExpiresAt();
      const nowSeconds = Math.floor(Date.now() / 1000);
      const isExpired =
        expiresAt !== null && expiresAt - nowSeconds < 60;

      if (isExpired) {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          try {
            const res = await fetch('/api/auth/refresh', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ refreshToken }),
            });
            const json = await res.json();
            if (res.ok && json.success !== false) {
              const data = json.data ?? json;
              setTokens(
                data.accessToken,
                data.refreshToken,
                data.expiresAt
              );
              setIsAuthenticated(true);
            } else {
              clearTokens();
              setIsAuthenticated(false);
            }
          } catch {
            clearTokens();
            setIsAuthenticated(false);
          }
        } else {
          clearTokens();
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(true);
      }

      setIsLoading(false);
    }

    initAuth();
  }, []);

  // Listen for auth:logout events (triggered by API on 401 refresh failure)
  useEffect(() => {
    const handleLogout = () => {
      clearTokens();
      setIsAuthenticated(false);
      navigate('/login');
    };

    window.addEventListener('auth:logout', handleLogout);
    return () =>
      window.removeEventListener(
        'auth:logout',
        handleLogout
      );
  }, [navigate]);

  const login = useCallback(
    (
      accessToken: string,
      refreshToken: string,
      expiresAt?: number
    ) => {
      setTokens(accessToken, refreshToken, expiresAt);
      setIsAuthenticated(true);
    },
    []
  );

  const logout = useCallback(() => {
    clearTokens();
    setIsAuthenticated(false);
    navigate('/login');
  }, [navigate]);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      'useAuth must be used within an AuthProvider'
    );
  }
  return context;
}
